import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, Clock, ChevronRight, X, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  } catch { return iso; }
};

const MissedClockoutCard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closingId, setClosingId] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/clock/admin/missed-clockouts`, { headers: authHeaders() });
      setRows(r.data?.rows || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(t);
  }, [load]);

  const handleClose = async (row) => {
    const ok = window.confirm(
      `Close ${row.employee_name}'s session?\n\nClock-IN: ${fmt(row.clock_in_at)}\nElapsed: ${row.elapsed_hours}h\n\nThis will create an OUT event timestamped now and mark this session as admin-closed.`
    );
    if (!ok) return;
    setClosingId(row.event_id);
    try {
      await axios.post(
        `${API}/api/clock/admin/close-missed/${row.event_id}`,
        { note: 'Closed by HR after missed clock-out alert' },
        { headers: authHeaders() }
      );
      toast({ title: 'Session closed', description: `${row.employee_name}'s open session has been closed.` });
      setRows(prev => prev.filter(r => r.event_id !== row.event_id));
    } catch (e) {
      toast({
        title: 'Could not close session',
        description: e?.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setClosingId(null);
    }
  };

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <Card className="border-rose-300 bg-gradient-to-r from-rose-50 to-orange-50 shadow-md" data-testid="missed-clockout-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="bg-rose-100 p-2 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-rose-700" />
            </div>
            <div>
              <h3 className="font-bold text-rose-900">Missed Clock-Out</h3>
              <p className="text-xs text-rose-700">{rows.length} employee{rows.length > 1 ? 's are' : ' is'} clocked in over 12 hours</p>
            </div>
          </div>
          <Link to="/clock-in-out">
            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-100" data-testid="missed-clockout-action">
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {rows.map(r => (
            <div
              key={r.event_id}
              className="bg-white rounded-lg p-2.5 border border-rose-200 flex items-center justify-between gap-2"
              data-testid={`missed-row-${r.employee_code}`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-gray-900 truncate">
                  {r.employee_name}
                  <span className="text-[10px] font-mono text-gray-400 ml-1">· {r.employee_code}</span>
                </p>
                <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                  <Clock className="h-3 w-3" /> IN @ {fmt(r.clock_in_at)} · {r.branch_name || '—'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-rose-700 leading-none">{r.elapsed_hours}h</p>
                <p className="text-[10px] text-gray-500">elapsed</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-100 h-8 px-2"
                onClick={() => handleClose(r)}
                disabled={closingId === r.event_id}
                title="Close this session manually (creates OUT event now)"
                data-testid={`close-missed-${r.employee_code}`}
              >
                {closingId === r.event_id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <>
                    <X className="h-3.5 w-3.5 mr-1" /> Close
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default MissedClockoutCard;
